import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'

// Prod serves the SPA on stayplay.integolf.com but the API on
// stayplayapi.integolf.com, and the SPA host does NOT proxy /api/* — so a
// relative '/api/...' URL returns index.html and the page crashes. Always hit
// the API origin directly (VITE_API_ORIGIN at build; window.origin in dev,
// where Vite proxies /api through).
const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || window.location.origin

/**
 * Customer my-trip portal for RESELLER bookings — Phase 10.
 * Mounted at /reseller-trip/:bookingId?t=<magicToken>.
 *
 * Separate from /my-trip (Stay & Play) per the don't-touch-Stay & Play
 * guardrail. Reads the booking via the magic token, shows itinerary +
 * the composed refund schedule, allows cancel.
 *
 * Reseller-branded throughout.
 */
export default function ResellerMyTrip() {
  const { bookingId } = useParams()
  const [params] = useSearchParams()
  const token = params.get('t')

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [cancelling, setCancelling] = useState(false)
  const [cancelResult, setCancelResult] = useState(null)

  function load() {
    setLoading(true)
    axios.get(`${API_ORIGIN}/api/public/reseller-ops/trip/${bookingId}?t=${token}`, { validateStatus: () => true })
      .then(r => {
        if (r.status >= 400) setError(r.data?.message || `Error ${r.status}`)
        else if (!r.data || typeof r.data !== 'object' || !r.data.booking) setError('Could not load your trip. Please use the most recent link from your confirmation email.')
        else setData(r.data)
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }
  useEffect(load, [bookingId, token])

  async function cancel() {
    if (!data?.refund?.applicable) {
      if (!confirm('No refund tier matches today; you may receive 0% back. Continue?')) return
    } else {
      const pct = data.refund.applicable.RefundPercent
      if (!confirm(`Cancel this trip? Per the refund schedule today, you would receive ${pct}% back.`)) return
    }
    setCancelling(true)
    try {
      const r = await axios.post(`${API_ORIGIN}/api/public/reseller-ops/trip/${bookingId}/cancel`,
        { token, reason: 'Customer self-cancel via my-trip portal' },
        { validateStatus: () => true });
      if (r.status >= 400) throw new Error(r.data?.message || 'Cancel failed');
      setCancelResult(r.data); load()
    } catch (e) { alert(e.message) } finally { setCancelling(false) }
  }

  if (loading) return <Frame brand={null}><div style={muted}>Loading…</div></Frame>
  if (error)   return <Frame brand={null}><div style={errorBox}>{error}</div></Frame>
  if (!data)   return null

  const { booking, rounds, addons, refund, brand } = data
  const primary = brand?.primaryColor || '#0f172a'
  // Standalone tee-time booking: no package, no hotel — just a round (or rounds).
  // Drives a leaner header + details block (single date, no rooms).
  const isTeeTimeOnly = !booking.Package_ID && !booking.HotelName

  return (
    <Frame brand={brand} reseller={data.booking}>
      {data.magicUrl && <AccountBanner hasAccount={data.hasAccount} magicUrl={data.magicUrl} primary={primary} />}
      <h1 style={{ margin: '0 0 8px', fontSize: 28 }}>
        {booking.PackageName || (isTeeTimeOnly ? 'Tee time booking' : 'Your trip')}
      </h1>
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: 24 }}>
        {booking.GroupingName ? `${booking.GroupingName} · ` : ''}Booking #{booking.ID_NO}
      </p>

      <StatusBanner status={booking.Status} />
      {cancelResult && (
        <div style={successBox}>
          Cancellation recorded. {cancelResult.refundedAmount != null
            ? <>You will receive a refund of <strong>${Number(cancelResult.refundedAmount).toFixed(2)}</strong> ({cancelResult.refundPercent}% of total).</>
            : <>No refund will be issued.</>}
        </div>
      )}

      <Section title="Trip details">
        <Row label="Customer" value={`${booking.GolferName} (${booking.GolferEmail})`} />
        {isTeeTimeOnly ? (
          <Row label="Date" value={booking.CheckInDate} />
        ) : (
          <>
            <Row label="Check-in" value={booking.CheckInDate} />
            <Row label="Check-out" value={booking.CheckOutDate} />
          </>
        )}
        <Row
          label={isTeeTimeOnly ? 'Golfers' : 'Golfers / Rooms'}
          value={isTeeTimeOnly
            ? `${booking.NumGolfers} golfer${booking.NumGolfers !== 1 ? 's' : ''}`
            : `${booking.NumGolfers} golfer${booking.NumGolfers !== 1 ? 's' : ''} · ${booking.NumRooms} room${booking.NumRooms !== 1 ? 's' : ''}`}
        />
        {booking.HotelName && <Row label="Hotel" value={booking.HotelName} />}
        <Row label="Total paid" value={`${booking.Currency} $${Number(booking.TotalCustomerPaid || 0).toFixed(2)}`} />
        {booking.RefundedAmount != null && <Row label="Refunded" value={`${booking.Currency} $${Number(booking.RefundedAmount).toFixed(2)}`} />}
      </Section>

      {rounds.length > 0 && (
        <Section title={`Golf rounds (${rounds.length})`}>
          {rounds.map(r => (
            <div key={r.ID_NO} style={{ padding: 12, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
              <div>
                <strong>{r.CourseName}</strong>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  {r.PlayDate} · {r.TeeTime || 'tee time TBD'}
                  {(r.GolfersOnRound || booking.NumGolfers)
                    ? ` · ${r.GolfersOnRound || booking.NumGolfers} golfer${(r.GolfersOnRound || booking.NumGolfers) !== 1 ? 's' : ''}`
                    : ''}
                </div>
                {r.Status === 'confirmed' && r.GolfBookingRef && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Confirmation: {r.GolfBookingRef}</div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                ${Number(r.TotalRoundPrice || 0).toFixed(2)}
                <div style={{ fontSize: 11, color: '#64748b' }}>{r.Status}</div>
              </div>
            </div>
          ))}
        </Section>
      )}

      {addons.length > 0 && (
        <Section title="Add-ons">
          {addons.map(a => (
            <div key={a.ID_NO} style={{ padding: 8, display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
              <span>{a.AddonName}</span><span>${Number(a.PriceTotal).toFixed(2)}</span>
            </div>
          ))}
        </Section>
      )}

      {(booking.Status === 'confirmed' || booking.Status === 'pending_payment') && (
        <Section title="Cancel + refund">
          {refund.tiers.length === 0
            ? <p style={muted}>No refund policy on file. Contact us if you need to cancel.</p>
            : (
              <>
                <p style={{ fontSize: 13, color: '#475569' }}>You're <strong>{refund.daysOut} day{refund.daysOut !== 1 ? 's' : ''}</strong> from check-in.</p>
                <ul style={{ fontSize: 13, color: '#475569', paddingLeft: 18 }}>
                  {refund.tiers.map((t, i) => {
                    const matches = refund.applicable && t.DaysBeforeMin === refund.applicable.DaysBeforeMin
                    return (
                      <li key={i} style={{ fontWeight: matches ? 700 : 400, color: matches ? '#0f172a' : '#475569' }}>
                        {tierLabel(t)}: {Number(t.RefundPercent)}% refund
                        {matches && ' ← applies today'}
                      </li>
                    )
                  })}
                </ul>
                <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>{refund.note}</p>
                <button onClick={cancel} disabled={cancelling} style={{ marginTop: 8, padding: '12px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: cancelling ? 0.6 : 1 }}>
                  {cancelling ? 'Cancelling…' : 'Cancel my trip'}
                </button>
              </>
            )
          }
        </Section>
      )}
    </Frame>
  )
}

// Top-of-page prompt: invite the customer to create an account (or log in if
// they already have one) to manage all their trips in one place. Both actions
// route through the magic-link landing, which auto-signs the token-authed
// customer in and prompts first-timers to set a password.
function AccountBanner({ hasAccount, magicUrl, primary }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      justifyContent: 'space-between', background: '#fff',
      border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 18px', marginBottom: 16,
    }}>
      <div style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.5 }}>
        {hasAccount
          ? <><strong>Welcome back!</strong> Log in to see and manage all your trips in one place.</>
          : <><strong>Create an account</strong> to manage all your trips, save your details, and rebook faster.</>}
      </div>
      <a
        href={magicUrl}
        style={{
          padding: '10px 18px', background: primary, color: '#fff',
          borderRadius: 6, fontSize: 14, fontWeight: 600, textDecoration: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {hasAccount ? 'Log in' : 'Create my account'}
      </a>
    </div>
  )
}

function Frame({ brand, reseller, children }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui, sans-serif' }}>
      <header style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        {brand?.logoUrl && <img src={brand.logoUrl} alt="brand" style={{ height: 36, maxWidth: 180, objectFit: 'contain' }} />}
        {!brand?.logoUrl && <strong style={{ fontSize: 18 }}>My trip</strong>}
        {brand?.supportEmail && <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>Need help? <a href={`mailto:${brand.supportEmail}`} style={{ color: brand.primaryColor || '#0f172a' }}>{brand.supportEmail}</a></span>}
      </header>
      <main style={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px' }}>{children}</main>
      <footer style={{ padding: 24, textAlign: 'center', fontSize: 12, color: '#94a3b8', borderTop: '1px solid #e2e8f0' }}>
        Powered by <a href="https://integolf.com" target="_blank" rel="noopener" style={{ color: '#00d19f', textDecoration: 'none', fontWeight: 600 }}>InteGolf</a>
      </footer>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#0f172a' }}>{title}</h3>
      {children}
    </div>
  )
}
function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', padding: '6px 0', fontSize: 14, borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ width: 160, color: '#64748b' }}>{label}</div>
      <div style={{ flex: 1, color: '#0f172a' }}>{value}</div>
    </div>
  )
}
function StatusBanner({ status }) {
  const colors = {
    pending_payment: { bg: '#fef3c7', fg: '#92400e', label: 'Awaiting payment' },
    confirmed:       { bg: '#dcfce7', fg: '#166534', label: 'Confirmed' },
    cancelled:       { bg: '#fee2e2', fg: '#991b1b', label: 'Cancelled' },
    partial_refund:  { bg: '#fef3c7', fg: '#92400e', label: 'Cancelled (partial refund)' },
    refunded:        { bg: '#fee2e2', fg: '#991b1b', label: 'Refunded' },
    completed:       { bg: '#dbeafe', fg: '#1e40af', label: 'Completed' },
    no_show:         { bg: '#fee2e2', fg: '#991b1b', label: 'No-show' },
    failed:          { bg: '#fee2e2', fg: '#991b1b', label: 'Failed' },
  }
  const c = colors[status] || colors.pending_payment
  return <div style={{ padding: 10, background: c.bg, color: c.fg, borderRadius: 6, fontSize: 13, fontWeight: 600, marginBottom: 16, textAlign: 'center' }}>{c.label}</div>
}

function tierLabel(t) {
  if (t.DaysBeforeMax == null) return `${t.DaysBeforeMin}+ days before`
  return `${t.DaysBeforeMin}–${t.DaysBeforeMax} days before`
}

const muted = { color: '#64748b', fontSize: 14 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6 }
const successBox = { padding: 12, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: 6, marginBottom: 16 }
